import { useState } from 'react';
import { ItemArt } from '../avatar/itemArt';
import { Bubble } from '../components/Bubble';
import { ItemCard } from '../components/ItemCard';
import { RarityBadge } from '../components/RarityBadge';
import { Sheet } from '../components/Sheet';
import { type CatalogItem, type Equipped, type ItemInstance, RARITIES, type Rarity, isMultiPart, slotLabel } from '../models/types';
import { ApiException } from '../services/apiClient';
import { POOR_SIGNAL_MESSAGE } from '../services/messages';
import { catalog } from '../state/catalog';
import { usePlayer } from '../state/player';
import { useShell } from '../state/shell';
import { colors, rarityColors } from '../theme';

export function InventoryScreen() {
  const player = usePlayer((s) => s.player);
  const error = usePlayer((s) => s.error);
  const [detail, setDetail] = useState<{ item: CatalogItem; instance: ItemInstance } | null>(null);

  let body;
  if (player) {
    body = <Grid inventory={player.inventory} wearing={new Set(Object.values(player.avatar.equipped))} onOpen={(item, instance) => setDetail({ item, instance })} />;
  } else if (error) {
    body = <div className="center-fill">Could not load your collection.<br />{error}</div>;
  } else {
    body = <div className="center-fill"><span className="spinner dark" role="progressbar" aria-label="Loading" /></div>;
  }

  return (
    <section className="screen" aria-label="Collection">
      <h1 className="app-bar">Collection</h1>
      {body}
      {detail && <ItemDetailSheet item={detail.item} instance={detail.instance} onClose={() => setDetail(null)} />}
    </section>
  );
}

/** Newest first by found time; copies found in the same second order by instance id, so both clients pick the same copy. */
const isNewer = (a: ItemInstance, b: ItemInstance) => (a.foundAtTs !== b.foundAtTs ? a.foundAtTs > b.foundAtTs : a.instanceId > b.instanceId);

function Grid({ inventory, wearing, onOpen }: { inventory: ItemInstance[]; wearing: Set<string>; onOpen: (i: CatalogItem, inst: ItemInstance) => void }) {
  if (inventory.length === 0) {
    return (
      <div className="center-fill">
        <Bubble style={{ maxWidth: 360, textAlign: 'center' }}>
          <div style={{ fontSize: 64 }} aria-hidden="true">🎒</div>
          <h2 className="t-title-l" style={{ margin: '12px 0 8px' }}>Nothing collected yet</h2>
          <p>Go scan! Everything you pick up lands here.</p>
        </Bubble>
      </div>
    );
  }
  // Grouped by rarity (best first), newest first within a group.
  // Copies of one item share a card, which shows how many are owned and opens the newest.
  const copies = new Map<string, number>();
  const newest = new Map<string, ItemInstance>();
  for (const i of inventory) {
    copies.set(i.itemId, (copies.get(i.itemId) ?? 0) + 1);
    const seen = newest.get(i.itemId);
    if (!seen || isNewer(i, seen)) newest.set(i.itemId, i);
  }
  const groups = new Map<Rarity, ItemInstance[]>();
  for (const i of newest.values()) groups.set(i.rarity, [...(groups.get(i.rarity) ?? []), i]);
  const ordered = [...RARITIES].reverse().filter((r) => groups.has(r));
  return (
    <div className="scroll" style={{ padding: '8px 16px 32px' }}>
      {ordered.map((rarity) => {
        const list = [...groups.get(rarity)!].sort((a, b) => b.foundAtTs - a.foundAtTs);
        return (
          <div key={rarity} data-testid={`group-${rarity}`}>
            <div className="group-head">
              <RarityBadge rarity={rarity} />
              <span style={{ color: rarityColors[rarity] }}>{list.reduce((n, i) => n + (copies.get(i.itemId) ?? 1), 0)}</span>
            </div>
            <div className="grid-2">
              {list.map((inst) => {
                const item = catalog.get(inst.itemId);
                return <ItemCard key={inst.instanceId} item={item} assembled={inst.assembled} wearing={wearing.has(inst.itemId)} count={copies.get(inst.itemId) ?? 1} onClick={() => onOpen(item, inst)} />;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** One owned item: what it is, where it is worn, Wear / Take off, and Drop here. */
function ItemDetailSheet({ item, instance, onClose }: { item: CatalogItem; instance: ItemInstance; onClose: () => void }) {
  const slot = item.slot;
  const worn = usePlayer((s) => (slot ? s.player?.avatar.equipped[slot] === item.id : false));
  const showToast = useShell((s) => s.showToast);
  const found = new Date(instance.foundAtTs * 1000);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [dropping, setDropping] = useState(false);

  const drop = async () => {
    setDropping(true);
    try {
      await usePlayer.getState().dropItem(instance.instanceId);
      showToast(`📍 Dropped ${item.name}. Anyone nearby can pick it up.`);
      onClose();
    } catch (e) {
      setDropping(false);
      setConfirmDrop(false);
      if (e instanceof ApiException && e.isItemNotFound) {
        showToast('That item is no longer in your collection.');
        void usePlayer.getState().refresh();
        onClose();
      } else if (e instanceof ApiException) {
        showToast(e.isAccuracyTooLow ? POOR_SIGNAL_MESSAGE : e.message);
      } else {
        showToast('Could not drop that right now. Check your connection and GPS.');
      }
    }
  };

  const setWorn = async (wear: boolean) => {
    if (!slot) return;
    const equipped: Equipped = { ...(usePlayer.getState().player?.avatar.equipped ?? {}) };
    if (wear) equipped[slot] = item.id;
    else delete equipped[slot];
    try {
      await usePlayer.getState().setEquipped(equipped);
    } catch {
      showToast("Couldn't save your outfit. Try again.");
    }
  };

  return (
    <Sheet label={item.name} onClose={onClose}>
      <Bubble color={item.palette} border={`4px solid ${rarityColors[item.rarity]}`} padding={24} className="item-hero" style={{ textAlign: 'center' }}>
        <ItemArt item={item} className="emoji" />
        <div className="t-headline" style={{ fontWeight: 900, marginTop: 8 }}>{item.name}</div>
        <div style={{ margin: '10px 0' }}><RarityBadge rarity={item.rarity} /></div>
        {slot && <div className="t-label" style={{ marginTop: 8 }}>Worn on: {slotLabel(slot)}</div>}
        <p style={{ marginTop: 14 }}>{item.description}</p>
        {isMultiPart(item) && (
          <div className="chips" style={{ marginTop: 14, justifyContent: 'center' }}>
            {item.parts.map((p) => (
              <span key={p.id} className="chip" style={{ background: colors.lime, color: colors.ink }}>✅ {p.name}</span>
            ))}
          </div>
        )}
        <p className="t-small" style={{ marginTop: 14, opacity: 0.6 }}>Found {found.getDate()}/{found.getMonth() + 1}/{found.getFullYear()}</p>
        {slot && (
          <div style={{ marginTop: 16 }}>
            {worn ? (
              <button type="button" className="btn outlined" onClick={() => void setWorn(false)}>⊖ Take off</button>
            ) : (
              <button type="button" className="btn filled" onClick={() => void setWorn(true)}>👕 Wear</button>
            )}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          {confirmDrop ? (
            <div role="group" aria-label="Confirm drop" data-testid="drop-confirm">
              <p style={{ marginBottom: 12 }}>Drop {item.name} here? Anyone who scans nearby can pick it up, including you. If nobody does within 7 days, it disappears for good.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button type="button" className="btn outlined" disabled={dropping} onClick={() => setConfirmDrop(false)}>Cancel</button>
                <button type="button" className="btn filled" disabled={dropping} onClick={() => void drop()}>
                  {dropping ? <span className="spinner" role="progressbar" aria-label="Dropping" /> : 'Drop'}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn outlined" onClick={() => setConfirmDrop(true)}>📍 Drop here</button>
          )}
        </div>
      </Bubble>
    </Sheet>
  );
}
